import { IconCollection } from "components/Icon/IconCollection";
import IconButton from "components/IconButton";
import ProfilePopover from "components/ProfilePopover";
import SettingsPopover from "components/SettingsPopover";
import Tooltip from "components/Tooltip/Tooltip";
import {
    getAuthCheckPublicInfo,
    getBackendVersion,
    getUserData,
} from "containers/Auth/Auth.selectors";
import { getDocumentationUrl } from "containers/Documentation/documentationService";
import { MessagesPopover } from "containers/Messages/components/MessagesPopover/MessagesPopover";
import SearchBar from "containers/SearchBar";
import { UploadPopover } from "containers/Upload/components/UploadPopover/UploadPopover";
import { useWorkspaceConfigValue } from "containers/WorkspaceConfig/useWorkspaceConfigValue";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { getWebsocketError } from "utils/NotificationManager/NotificationManager.selectors";
import { mapScreensToPaths, useMatchExecutionFilterLocation } from "utils/RouterManager/utils";
import Logo from "../../../assets/svg/logo.svg";
import LogoStacked from "../../../assets/svg/logo_stacked.svg";
import "./Topbar.scss";

export const versionString = `${process.env.REACT_APP_VERSION_MAJOR}+${process.env.REACT_APP_VERSION_BRANCH_NAME}.${process.env.REACT_APP_VERSION_BUILD_DATE}.${process.env.REACT_APP_VERSION_SHORT_SHA}`;
const Topbar = () => {
    const history = useHistory();
    const { executionFilterLocation, executionFilterLocationId } = useMatchExecutionFilterLocation();
    const userData = useSelector(getUserData);
    const backendVersion = useSelector(getBackendVersion);
    const authCheckPublicInfo = useSelector(getAuthCheckPublicInfo);
    const backendVersionForTooltip = backendVersion ?? authCheckPublicInfo?.backendVersion ?? "—";
    const workspaceID = userData?.workspace_id;

    const hasWebsocketError = useSelector(getWebsocketError);

    const isExecutionProcessorEnabled = useWorkspaceConfigValue("EXECUTION_PROCESSOR_ENABLED");
    const maxParallelExecutions = useWorkspaceConfigValue("EXECUTIONS_MAX_PARALLEL");

    const isWorkspaceProcessExecutionsDisabled = useMemo(
        () => isExecutionProcessorEnabled === false && maxParallelExecutions === 0,
        [isExecutionProcessorEnabled, maxParallelExecutions]
    );
    const workspaceHighlightColorFromConfig = useWorkspaceConfigValue("WORKSPACE_HIGHLIGHT_COLOR");
    const workspaceHighlightColor =
        workspaceHighlightColorFromConfig ?? authCheckPublicInfo?.workspaceHighlightColor;

    const notification = useMemo(() => {
        if (isWorkspaceProcessExecutionsDisabled) {
            return (
                <div className="topbar__warning">
                    <span>
                        WARNING: this workspace is currently not processing executions.{" "}
                        <Link to={mapScreensToPaths.workspaceSettings(workspaceID)}>
                            Go to workspace settings
                        </Link>
                    </span>
                </div>
            );
        } else {
            return null;
        }
    }, [isWorkspaceProcessExecutionsDisabled, workspaceID]);

    return (
        <div className="topbarWrapper" style={{ background: workspaceHighlightColor }}>
            {hasWebsocketError && (
                <div className="topbar__notification topbar__notification-error">
                    <div className="topbar__warning">
                        <span>
                            WARNING: Connection to backend lost. Cannot perform actions or save
                            changes. Trying to reconnect.
                        </span>
                    </div>
                </div>
            )}
            {notification && <div className="topbar__notification">{notification}</div>}

            <div className="topbar">
                <div className="logo__wrap">
                    <Link to="/" className="logo">
                        <img
                            className="logo_wide"
                            src={Logo}
                            alt="Cloudomation logo"
                            title={`Frontend version: ${versionString}\nBackend version: ${backendVersionForTooltip}`}
                        />
                        <img
                            className="logo_stacked"
                            src={LogoStacked}
                            alt="Cloudomation logo"
                            title={`Frontend version: ${versionString}\nBackend version: ${backendVersionForTooltip}`}
                        />
                    </Link>
                </div>
                <SearchBar />
                <div className="topbar__end">
                    <UploadPopover />

                    <Tooltip overlay="Executions Ctrl+M">
                        <IconButton
                            icon={IconCollection.flashOutline}
                            onClick={() => {
                                const isMainExecutionFilterScreen =
                                    executionFilterLocation === "workspace" &&
                                    !!executionFilterLocationId;
                                if (!isMainExecutionFilterScreen) {
                                    history.push(
                                        mapScreensToPaths.executionFilter({ id: workspaceID })
                                    );
                                }
                            }}
                            data-testid="executionFilterPageHeaderLink"
                        />
                    </Tooltip>
                    <Tooltip overlay="Messages">
                        <MessagesPopover />
                    </Tooltip>
                    <Tooltip overlay="Docs">
                        <IconButton
                            icon={IconCollection.helpCircleOutline}
                            onClick={() => window.open(getDocumentationUrl())}
                        />
                    </Tooltip>
                    <Tooltip overlay="User Management">
                        <ProfilePopover />
                    </Tooltip>
                    <Tooltip overlay="Settings">
                        <SettingsPopover workspaceID={workspaceID} />
                    </Tooltip>
                </div>
            </div>
        </div>
    );
};

export default Topbar;
